import { show } from "@intercom/messenger-js-sdk";
import CodeIcon from "@mui/icons-material/Code";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { AppBar as MuiAppBar, Container } from "@mui/material";
import { type FC } from "react";
import { matchPath, NavLink, useLocation } from "react-router";

import { MobileMenuButton } from "~/components/AppBar/MobileMenuButton";
import { MobileSearch } from "~/components/AppBar/MobileSearch";
import { MobileUserButton } from "~/components/AppBar/MobileUserButton";
import { MoreIndicesButton } from "~/components/AppBar/MoreIndicesButton";
import { UserButton } from "~/components/AppBar/UserButton";
import { AppLogo } from "~/components/AppLogo/AppLogo";
import { ColorModeButton } from "~/components/ColorModeButton";
import { MobileBar } from "~/components/MobileBar";
import { MoreButton } from "~/components/UI/MoreButton";
import { RichTooltip } from "~/components/UI/RichTooltip";
import { ROUTES } from "~/constants/routing";
import { PLATFORM_TOUR_LINK } from "~/constants/socials";
import { MoreDashboardsButton } from "~/feature/dashboards/components/MoreDashboardsButton";
import { GetUpdatesAppBarButton } from "~/feature/getUpdates/components/GetUpdatesAppBarButton";
import { AppSearch } from "~/feature/search/components/AppSearch";

import * as Styled from "./AppBar.styles";

const BETA_TOOLTIP =
  "Prediction Hero is currently in beta. You may encounter minor issues as we continue improving the platform. Your feedback is always welcome.";
const BETA_LINK_TEXT = "Give us feedback";

export const AppBar: FC = () => {
  const { pathname } = useLocation();
  const isInsightsActive = !!matchPath(
    { path: ROUTES.insights.path, end: false },
    pathname
  );
  const isApiActive = !!matchPath({ path: ROUTES.data.path }, pathname);
  const isTourActive = !!matchPath({ path: PLATFORM_TOUR_LINK }, pathname);

  return (
    <Styled.Root>
      <MuiAppBar position="static" color="transparent" elevation={0}>
        <Container maxWidth="xl">
          <Styled.Toolbar disableGutters>
            <Styled.Wrapper gap="8px">
              <AppLogo />
              <RichTooltip
                title={BETA_TOOLTIP}
                actionText={BETA_LINK_TEXT}
                action={show}
              >
                <Styled.ChipBeta
                  label="BETA"
                  color="info"
                  size="small"
                  data-skip-custom-styles="true"
                />
              </RichTooltip>
            </Styled.Wrapper>
            <Styled.EmptyBox />
            <Styled.DesktopOnly>
              <Styled.NavRow>
                <Styled.Nav>
                  <Styled.IconButtonWrapper>
                    <MoreDashboardsButton />
                  </Styled.IconButtonWrapper>
                  <Styled.IconButtonWrapper>
                    <MoreButton
                      text="Insights"
                      startIcon={<QueryStatsIcon fontSize="small" />}
                      arrowIcon={false}
                      component={NavLink}
                      aria-label="go to insights"
                      to={ROUTES.insights.createURL()}
                      prefetch="intent"
                      isActive={isInsightsActive}
                    />
                  </Styled.IconButtonWrapper>
                  <Styled.IconButtonWrapper>
                    <MoreIndicesButton />
                  </Styled.IconButtonWrapper>
                  <Styled.IconButtonWrapper>
                    <MoreButton
                      text="API"
                      startIcon={<CodeIcon fontSize="small" />}
                      arrowIcon={false}
                      component={NavLink}
                      aria-label="go to API & data licensing"
                      to={ROUTES.data.createURL()}
                      prefetch="intent"
                      isActive={isApiActive}
                    />
                  </Styled.IconButtonWrapper>
                  <Styled.IconButtonWrapper>
                    <MoreButton
                      text="Tour"
                      startIcon={<ExploreOutlinedIcon fontSize="small" />}
                      arrowIcon={false}
                      component={NavLink}
                      aria-label="go to platform tour"
                      to={PLATFORM_TOUR_LINK}
                      isActive={isTourActive}
                      sx={(theme) => ({
                        "&, &.active": {
                          borderBottomColor: theme.palette.gold.dark,
                        },
                        "& .MuiTypography-root, & .MuiButton-startIcon": {
                          color: theme.palette.gold.dark,
                        },
                      })}
                    />
                  </Styled.IconButtonWrapper>
                </Styled.Nav>
                <AppSearch />
                <Styled.IconButtons>
                  <ColorModeButton />
                </Styled.IconButtons>
                <GetUpdatesAppBarButton />
                <UserButton />
              </Styled.NavRow>
            </Styled.DesktopOnly>
            <Styled.MobileOnly>
              <MobileSearch />
              <MobileUserButton />
              <MobileMenuButton />
            </Styled.MobileOnly>
          </Styled.Toolbar>
        </Container>
      </MuiAppBar>
      <MobileBar />
    </Styled.Root>
  );
};
